import { BlockNote } from './BlockNote';
import { BloomType, ClientSettings, ExerciseLevel, ExerciseTool } from './Exercise';

export const emptyBlockContent = '<p><br></p>';

export type StudyGuideBlockContent = {
  question: string;
  concepts: string;
  summary: string;
};

export const emptyStudyGuideBlockContent = {
  concepts: '<ul><li></li></ul>',
  question: '',
  summary: '',
};

export enum BlockType {
  H5P = 'h5p',
  BOOK_WIDGET = 'book_widget',
  IFRAME = 'iframe',
  WYSIWYG = 'wysiwyg',
  VIDEO = 'video',
  STUDY_GUIDE = 'study_guide',
  ALGEBRA_KIT = 'algebra_kit',
  COLUMN = 'column',
  CLUSTER = 'cluster',
  SECTION = 'section',
  TEXT_EXERCISE = 'text_exercise',
}

export type BackendBlock = {
  id: string;
  title?: string;
  type: BlockType;
  content: string | StudyGuideBlockContent;
  level?: ExerciseLevel;
  created_at: string;
  updated_at: string;
  bloom_type?: BloomType;
  client_settings?: ClientSettings;
  url?: string;
  algebra_kit_exercise_id?: string;
  parent_id?: string | undefined;
  is_printable?: boolean;
  is_visible?: boolean;
  columns?: WysiwygColumn[];
  position?: number;
  studentLatestScore?: {
    score: number | null;
    maxScore: number | null;
  };
  childBlocks?: BackendBlock[];
  exercise_tool: ExerciseTool;
};

// column block types
export type WysiwygColumn = {
  id: string;
  type: BlockType;
  title: string;
  client_settings: ClientSettings;
  is_visible: boolean;
  content: string;
  parent_id: string | null;
  is_printable?: boolean;
};

export type ColumnBlock = {
  id: string;
  type: BlockType.COLUMN;
  title: string;
  client_settings: Record<string, any>;
  is_visible: boolean;
  parent_id: string;
  is_printable: boolean;
  columns: WysiwygColumn[];
  amountOfColumns?: number;
};

export type SectionBlock = {
  id: string;
  type: BlockType.SECTION;
  title: string;
  client_settings: Record<string, any>;
  is_visible: boolean;
  parent_id: string;
  is_printable: boolean;
  childBlocks: BackendBlock[];
  exercise_tool: ExerciseTool;
  metadata: any; // TODO - retype this ASAP we have more info!!!
};

// FRONTEND

export type BlockWithNotes = Block & {
  note?: BlockNote | undefined;
  pageType?: string | undefined;
};

export const isEmptyBlock = (block: Block): boolean => {
  if (block.type === BlockType.WYSIWYG) return !block.content || block.content === emptyBlockContent;
  if ([BlockType.H5P, BlockType.BOOK_WIDGET].indexOf(block.type) >= 0) return !block.url;
  if (block.type === BlockType.ALGEBRA_KIT) return !block.algebra_kit_exercise_id;
  if ([BlockType.VIDEO, BlockType.IFRAME].indexOf(block.type) >= 0) return !block.content && !block.url;
  if (block.type === BlockType.COLUMN) return !block.columns || block.columns.every(column => !column.content || column.content === emptyBlockContent);
  if (block.type === BlockType.TEXT_EXERCISE) return !block.description || block.description === emptyBlockContent;
  if (block.type === BlockType.SECTION) return !block.childBlocks || block.childBlocks.every(childBlock => isEmptyBlock(childBlock));
  if (block.type === BlockType.CLUSTER) return !block.sections || block.sections.every(childBlock => isEmptyBlock(childBlock));

  return true;
}

export type Block = Omit<
  BackendBlock,
  'created_at' | 'updated_at' | 'bloom_type' | 'client_settings' | 'content' | 'childBlocks'
> & {
  bloom_type?: string;
  client_settings?: ClientSettings;
  content?: string;
  studyGuideContent?: StudyGuideBlockContent;
  algebra_kit_exercise_id?: string;
  parent_id?: string | undefined;
  is_printable?: boolean;
  amountOfColumns?: number;
  studentLatestScore?: {
    score: number;
    maxScore: number;
  };
  sections?: Block[];
  is_defaultsection?: boolean;
  childBlocks?: Block[];
  exercise_tool: ExerciseTool;
  description?: string;
  hideClusterTitle?: boolean;
};

export type BlockListIds =
  | {
      id: string;
    }
  | { id: string; childIds: string[] | undefined };

// mapBlocksListToIds

export const mapBlocksListToIds = (
  blocksList: Block[]
): { id: string; sections: BlockListIds[] }[] | { id: string }[] => {
  const blocksListIds = blocksList.map((pageBlock) => {
    if (pageBlock.type !== 'cluster') return { id: pageBlock.id };
    return {
      id: pageBlock.id,
      sections: pageBlock!.sections!.map((section) => mapSectionToListIds(section)),
    };
  });
  return blocksListIds;
};

export const mapSectionToListIds = (section: Block): BlockListIds => {
  const blocksListIds = section.childBlocks!.map((block) => block.id);
  return { id: section.id, childIds: blocksListIds };
};
